import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Play, Download, Plus, Trash2, Edit2, Settings, 
  Music, FileText, Clock, Palette, Type, Save, GripVertical,
  ChevronLeft, ChevronRight, FileDown, Sparkles, Eye, Image, Monitor, Copy,
  Languages, Globe, Undo2, Redo2, Share2, Users2, MessageSquare
} from "lucide-react";
import WallpaperSelector from "@/components/WallpaperSelector";
import BrandingTemplateGallery from "@/components/BrandingTemplateGallery";
import BrandingSettings from "@/components/editor/BrandingSettings";
import BrandingPreviewModal from "@/components/editor/BrandingPreviewModal";
import SharePresentationDialog from "@/components/SharePresentationDialog";
import ProjectMembersDialog from "@/components/ProjectMembersDialog";
import ProjectCommentsDialog from "@/components/ProjectCommentsDialog";
import PresenceAvatars from "@/components/PresenceAvatars";
import { useAuth } from "@/contexts/AuthContext";
import { AppTour, EDITOR_TOUR_STEPS } from "@/components/AppTour";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLIDE_TYPES = [
  { value: "text", label: "Text", color: "#000000" },
  { value: "title", label: "Title", color: "#000000" },
  { value: "verse", label: "Verse", color: "#000000" },
  { value: "chorus", label: "Chorus", color: "#1e1b4b" },
  { value: "bridge", label: "Bridge", color: "#0f172a" }
];

// Maximum history states to keep
const MAX_HISTORY = 50;

const Editor = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [wallpaperDialogOpen, setWallpaperDialogOpen] = useState(false);
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [brandingPreviewOpen, setBrandingPreviewOpen] = useState(false);
  const [brandingGalleryOpen, setBrandingGalleryOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState("es");
  const [slideTemplates, setSlideTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  
  // Current slide edit state
  const [editSlide, setEditSlide] = useState({
    content: "",
    type: "text",
    background_color: "#000000",
    background_image: null,
    background_type: "color",
    text_color: "#ffffff",
    font_size: 48
  });

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
      if (response.data.slides?.length > 0) {
        setSelectedSlideIndex(0);
      }
    } catch (error) {
      toast.error("Failed to load project");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  const fetchSlideTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/slide-templates`);
      setSlideTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates");
    }
  }, []);

  useEffect(() => {
    fetchProject();
    fetchSlideTemplates();
  }, [fetchProject, fetchSlideTemplates]);

  // Initialize history when project loads
  useEffect(() => {
    if (project?.slides && history.length === 0) {
      setHistory([JSON.stringify(project.slides)]);
      setHistoryIndex(0);
    }
  }, [project?.id]); // Only run once when project loads

  // Add to history when slides change (but not during undo/redo)
  const addToHistory = useCallback((slides) => {
    if (isUndoRedo) {
      setIsUndoRedo(false);
      return;
    }
    
    const slidesJson = JSON.stringify(slides);
    
    // Don't add if same as current
    if (history[historyIndex] === slidesJson) return;
    
    // Remove any future history if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(slidesJson);
    
    // Limit history size
    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift();
    }
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex, isUndoRedo]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    
    setIsUndoRedo(true);
    const newIndex = historyIndex - 1;
    const previousSlides = JSON.parse(history[newIndex]);
    
    setHistoryIndex(newIndex);
    setProject(prev => ({ ...prev, slides: previousSlides }));
    saveProject({ slides: previousSlides });
    toast.success("Undo successful");
  }, [history, historyIndex]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    
    setIsUndoRedo(true);
    const newIndex = historyIndex + 1;
    const nextSlides = JSON.parse(history[newIndex]);
    
    setHistoryIndex(newIndex);
    setProject(prev => ({ ...prev, slides: nextSlides }));
    saveProject({ slides: nextSlides });
    toast.success("Redo successful");
  }, [history, historyIndex]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Undo: Ctrl+Z or Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Redo: Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
      // Duplicate slide: Ctrl+D or Cmd+D
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        handleDuplicateSlide(selectedSlideIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, selectedSlideIndex]);

  // Duplicate slide function
  const handleDuplicateSlide = (index) => {
    if (!project?.slides || index < 0 || index >= project.slides.length) return;
    
    const slideToDuplicate = project.slides[index];
    const duplicatedSlide = {
      ...slideToDuplicate,
      id: crypto.randomUUID() // Generate new unique ID
    };
    
    // Insert after the current slide
    const updatedSlides = [
      ...project.slides.slice(0, index + 1),
      duplicatedSlide,
      ...project.slides.slice(index + 1)
    ];
    
    setProject({ ...project, slides: updatedSlides });
    saveProject({ slides: updatedSlides });
    addToHistory(updatedSlides);
    setSelectedSlideIndex(index + 1); // Select the duplicated slide
    toast.success("Slide duplicated");
  };

  const saveProject = async (updatedProject) => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, updatedProject);
      setProject({ ...project, ...updatedProject });
      toast.success("Saved!");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSlide = () => {
    const newSlide = {
      id: crypto.randomUUID(),
      content: editSlide.content || "New Slide",
      type: editSlide.type,
      background_color: editSlide.background_color,
      background_image: editSlide.background_image,
      background_type: editSlide.background_type || "color",
      text_color: editSlide.text_color,
      font_size: editSlide.font_size
    };
    
    const updatedSlides = [...(project.slides || []), newSlide];
    saveProject({ slides: updatedSlides });
    addToHistory(updatedSlides);
    setSelectedSlideIndex(updatedSlides.length - 1);
    setAddDialogOpen(false);
    setSelectedTemplate(null);
    setEditSlide({
      content: "",
      type: "text",
      background_color: "#000000",
      background_image: null,
      background_type: "color",
      text_color: "#ffffff",
      font_size: 48
    });
  };

  const handleAddFromTemplate = (template) => {
    const newSlide = {
      id: crypto.randomUUID(),
      content: template.default_content,
      type: template.default_type,
      background_color: template.preview_color,
      background_image: null,
      background_type: "color",
      text_color: "#ffffff",
      font_size: template.default_font_size
    };
    
    const updatedSlides = [...(project.slides || []), newSlide];
    saveProject({ slides: updatedSlides });
    setSelectedSlideIndex(updatedSlides.length - 1);
    setAddDialogOpen(false);
    toast.success(`${template.name} added`);
  };

  const handleUpdateSlide = () => {
    const updatedSlides = [...project.slides];
    updatedSlides[selectedSlideIndex] = {
      ...updatedSlides[selectedSlideIndex],
      ...editSlide
    };
    saveProject({ slides: updatedSlides });
    setEditDialogOpen(false);
  };

  const handleDeleteSlide = (index) => {
    const updatedSlides = project.slides.filter((_, i) => i !== index);
    saveProject({ slides: updatedSlides });
    addToHistory(updatedSlides);
    if (selectedSlideIndex >= updatedSlides.length) {
      setSelectedSlideIndex(Math.max(0, updatedSlides.length - 1));
    }
  };

  const handleReorder = (newOrder) => {
    saveProject({ slides: newOrder });
    addToHistory(newOrder);
  };

  const openEditDialog = (slide) => {
    setEditSlide({ ...slide });
    setEditDialogOpen(true);
  };

  const handleGenerate = async () => {
    if (!textInput.trim()) {
      toast.error("Please enter some text");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/generate`, {
        content: textInput,
        is_song: project.is_song
      });
      setProject(response.data);
      toast.success("Slides generated!");
      setGenerateDialogOpen(false);
      setTextInput("");
    } catch (error) {
      toast.error("Failed to generate slides");
    } finally {
      setIsGenerating(false);
    }
  };

  // Translation functions
  const handleTranslateSlide = async () => {
    if (!currentSlide) return;
    
    setIsTranslating(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/translate-slide`, {
        slide_id: currentSlide.id,
        target_language: targetLanguage,
        source_language: project.primary_language || "en"
      });
      
      // Update local state with translation
      const updatedSlides = [...project.slides];
      updatedSlides[selectedSlideIndex] = {
        ...updatedSlides[selectedSlideIndex],
        secondary_content: response.data.translated_content,
        secondary_language: targetLanguage
      };
      setProject({ ...project, slides: updatedSlides });
      
      toast.success(t('translation_complete'));
      setTranslateDialogOpen(false);
    } catch (error) {
      toast.error("Translation failed. Please try again.");
    } finally {
      setIsTranslating(false);
    }
  };

  const handleTranslateAll = async () => {
    setIsTranslating(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/translate-all`, {
        target_language: targetLanguage,
        source_language: project.primary_language || "en"
      });
      
      // Refresh project to get all translations
      await fetchProject();
      
      toast.success(`${t('translation_complete')} (${response.data.success}/${response.data.total})`);
      setTranslateDialogOpen(false);
    } catch (error) {
      toast.error("Translation failed. Please try again.");
    } finally {
      setIsTranslating(false);
    }
  };

  const toggleBilingualMode = async (enabled) => {
    const updatedProject = {
      ...project,
      bilingual_mode: enabled
    };
    setProject(updatedProject);
    await saveProject({ bilingual_mode: enabled });
  };

  const setBilingualLayout = async (layout) => {
    const updatedProject = {
      ...project,
      bilingual_layout: layout
    };
    setProject(updatedProject);
    await saveProject({ bilingual_layout: layout });
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name}.${format === 'pptx' ? 'pptx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      // Handle feature gating error
      if (error.response?.status === 403 && error.response?.data?.detail?.error === 'feature_not_available') {
        toast.error(error.response.data.detail.message, {
          action: {
            label: t('upgrade'),
            onClick: () => navigate('/pricing')
          },
          duration: 8000
        });
      } else {
        toast.error("Export failed");
      }
    }
  };

  const currentSlide = project?.slides?.[selectedSlideIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-100 dark:bg-slate-950 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
        <div className="px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard-btn"
              className="flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              {project.is_song ? (
                <Music className="w-5 h-5 text-amber-500 flex-shrink-0" />
              ) : (
                <FileText className="w-5 h-5 text-indigo-500 flex-shrink-0" />
              )}
              <Input
                value={project.name}
                onChange={(e) => setProject({ ...project, name: e.target.value })}
                onBlur={() => saveProject({ name: project.name })}
                className="border-none bg-transparent font-heading font-semibold text-base sm:text-lg w-32 sm:w-auto min-w-0"
                data-testid="project-name-edit"
              />
            </div>
            {saving && <span className="hidden sm:inline text-sm text-muted-foreground">{t('saving') || 'Saving...'}</span>}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto max-w-full">
            {/* Undo/Redo Buttons */}
            <div className="hidden sm:flex items-center border rounded-md">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                className="rounded-r-none"
                title={t('undo') + ' (Ctrl+Z)'}
                data-testid="undo-btn"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                className="rounded-l-none border-l"
                title={t('redo') + ' (Ctrl+Shift+Z)'}
                data-testid="redo-btn"
              >
                <Redo2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Language Selector — hidden on mobile */}
            <span className="hidden md:inline-flex">
              <LanguageSelector variant="ghost" />
            </span>

            {/* Translate Button */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setTranslateDialogOpen(true)}
              className="gap-2 flex-shrink-0"
              data-testid="translate-btn"
            >
              <Languages className="w-4 h-4" />
              <span className="hidden md:inline">{t('translate')}</span>
            </Button>

            {/* Stream URL Copy */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const streamUrl = `${window.location.origin}/stream/${projectId}`;
                navigator.clipboard.writeText(streamUrl);
                toast.success(t('stream_url_copied') || 'Stream URL copied! Use in OBS Browser Source');
              }}
              className="gap-2 flex-shrink-0 hidden sm:inline-flex"
              data-testid="copy-stream-url-btn"
              title={t('copy_stream_url') || 'Copy URL for OBS/streaming'}
            >
              <Monitor className="w-4 h-4" />
              <span className="hidden md:inline">{t('stream_url')}</span>
            </Button>

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSettingsDialogOpen(true)}
              className="gap-2 flex-shrink-0"
              data-testid="settings-btn"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">{t('settings')}</span>
            </Button>
            
            <Select onValueChange={(value) => handleExport(value)}>
              <SelectTrigger className="w-auto md:w-[130px] flex-shrink-0" data-testid="export-dropdown">
                <FileDown className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">{t('export')}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf" data-testid="export-pdf">Export as PDF</SelectItem>
                <SelectItem value="pptx" data-testid="export-pptx">Export as PPTX</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              onClick={() => navigate(`/present/${projectId}`)}
              className="gap-2 bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
              data-testid="present-btn"
            >
              <Play className="w-4 h-4" />
              <span className="hidden sm:inline">{t('present')}</span>
            </Button>

            <Button
              onClick={() => setShareDialogOpen(true)}
              variant="outline"
              className="gap-2 flex-shrink-0"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden lg:inline">Share</span>
            </Button>

            <Button
              onClick={() => setMembersDialogOpen(true)}
              variant="outline"
              className="gap-2 flex-shrink-0"
              data-testid="members-btn"
            >
              <Users2 className="w-4 h-4" />
              <span className="hidden lg:inline">Members</span>
            </Button>

            <Button
              onClick={() => setCommentsDialogOpen(true)}
              variant="outline"
              className="gap-2 flex-shrink-0"
              data-testid="comments-btn"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden lg:inline">Comments</span>
            </Button>

            <PresenceAvatars
              projectId={projectId}
              currentUserId={user?.user_id || user?.id}
            />
          </div>
        </div>
      </header>

      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Slides Panel (Left on desktop, top strip on mobile) */}
        <div className="w-full md:w-64 md:h-full h-auto max-h-[35vh] md:max-h-none bg-white dark:bg-slate-900 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 flex flex-col flex-shrink-0">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Slides</h3>
              <span className="text-xs text-muted-foreground">{project.slides?.length || 0}</span>
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 gap-1"
                onClick={() => setAddDialogOpen(true)}
                data-testid="add-slide-btn"
              >
                <Plus className="w-4 h-4" />
                Add
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 gap-1"
                onClick={() => setGenerateDialogOpen(true)}
                data-testid="generate-btn"
              >
                <Sparkles className="w-4 h-4" />
                Generate
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-3">
              {project.slides?.length > 0 ? (
                <Reorder.Group axis="y" values={project.slides} onReorder={handleReorder}>
                  {project.slides.map((slide, index) => (
                    <Reorder.Item key={slide.id} value={slide}>
                      <motion.div
                        className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          selectedSlideIndex === index 
                            ? 'border-amber-500 shadow-lg' 
                            : 'border-transparent hover:border-slate-300'
                        }`}
                        onClick={() => setSelectedSlideIndex(index)}
                        data-testid={`slide-thumbnail-${index}`}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div 
                          className="aspect-video flex items-center justify-center p-2 text-center relative"
                          style={{ 
                            backgroundColor: slide.background_color,
                            color: slide.text_color,
                            ...(slide.background_type === 'gradient' && slide.background_image && {
                              background: slide.background_image
                            })
                          }}
                        >
                          {/* Background Video for thumbnails (shows first frame) */}
                          {slide.background_type === 'video' && slide.background_video && (
                            <video 
                              src={slide.background_video}
                              className="absolute inset-0 w-full h-full object-cover"
                              muted
                              playsInline
                            />
                          )}
                          {/* Background Image for thumbnails */}
                          {slide.background_type === 'image' && slide.background_image && (
                            <div 
                              className="absolute inset-0 bg-cover bg-center"
                              style={{ backgroundImage: `url(${slide.background_image})` }}
                            />
                          )}
                          {/* Background GIF for thumbnails */}
                          {slide.background_type === 'gif' && slide.background_image && (
                            <img 
                              src={slide.background_image}
                              alt=""
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          )}
                          {/* Overlay for readability on image/gradient/video/gif backgrounds */}
                          {(slide.background_type === 'image' || slide.background_type === 'gradient' || slide.background_type === 'video' || slide.background_type === 'gif') && (slide.background_image || slide.background_video) && (
                            <div className="absolute inset-0 bg-black/30" />
                          )}
                          <p className="text-xs font-lyrics line-clamp-3 relative z-10 drop-shadow-md">{slide.content}</p>
                        </div>
                        
                        {/* Slide number */}
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/50 rounded text-xs text-white">
                          {index + 1}
                        </div>

                        {/* Video indicator */}
                        {slide.background_type === 'video' && (
                          <div className="absolute top-1 left-8 px-1.5 py-0.5 bg-amber-500 rounded text-xs text-black flex items-center gap-0.5">
                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        )}
                        
                        {/* GIF indicator */}
                        {slide.background_type === 'gif' && (
                          <div className="absolute top-1 left-8 px-1.5 py-0.5 bg-purple-500 rounded text-xs text-white font-bold">
                            GIF
                          </div>
                        )}

                        {/* Type badge */}
                        {slide.type && slide.type !== 'text' && (
                          <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-xs ${
                            slide.type === 'chorus' ? 'bg-amber-500 text-black' : 'bg-white/20 text-white'
                          }`}>
                            {slide.type}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="w-6 h-6 bg-black/50 hover:bg-black/70"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateSlide(index);
                            }}
                            title={t('duplicate_slide') + ' (Ctrl+D)'}
                            data-testid={`duplicate-slide-${index}`}
                          >
                            <Copy className="w-3 h-3 text-white" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="w-6 h-6 bg-black/50 hover:bg-black/70"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(slide);
                            }}
                          >
                            <Edit2 className="w-3 h-3 text-white" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="w-6 h-6 bg-black/50 hover:bg-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSlide(index);
                            }}
                            data-testid={`delete-slide-${index}`}
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </Button>
                        </div>

                        {/* Drag handle */}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                          <GripVertical className="w-4 h-4 text-white/50" />
                        </div>
                      </motion.div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              ) : (
                <div className="text-center py-10 text-muted-foreground">
                  <p className="text-sm mb-4">{t('no_slides_yet') || 'No slides yet'}</p>
                  <Button 
                    size="sm" 
                    onClick={() => setGenerateDialogOpen(true)}
                    className="gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    {t('generate_with_ai') || 'Generate with AI'}
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Preview Panel (Center) */}
        <div className="flex-1 flex flex-col p-4 sm:p-8 min-w-0 overflow-hidden">
          {currentSlide ? (
            <>
              {/* Slide Preview */}
              <div className="flex-1 flex items-center justify-center min-h-0">
                <motion.div
                  key={selectedSlideIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="w-full max-w-4xl aspect-video rounded-2xl shadow-2xl overflow-hidden relative"
                  style={{ 
                    backgroundColor: currentSlide.background_color,
                    color: currentSlide.text_color,
                    ...(currentSlide.background_type === 'gradient' && currentSlide.background_image && {
                      background: currentSlide.background_image
                    })
                  }}
                  data-testid="slide-preview"
                >
                  {/* Background Video */}
                  {currentSlide.background_type === 'video' && currentSlide.background_video && (
                    <video 
                      src={currentSlide.background_video}
                      className="absolute inset-0 w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  )}
                  {/* Background Image */}
                  {currentSlide.background_type === 'image' && currentSlide.background_image && (
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${currentSlide.background_image})` }}
                    />
                  )}
                  {/* Background GIF */}
                  {currentSlide.background_type === 'gif' && currentSlide.background_image && (
                    <img 
                      src={currentSlide.background_image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  {/* Overlay for readability */}
                  {(currentSlide.background_type === 'image' || currentSlide.background_type === 'gradient' || currentSlide.background_type === 'video' || currentSlide.background_type === 'gif') && (currentSlide.background_image || currentSlide.background_video) && (
                    <div className="absolute inset-0 bg-black/30" />
                  )}
                  
                  {/* Content - supports bilingual mode */}
                  <div className={`w-full h-full flex ${
                    project?.bilingual_mode && currentSlide.secondary_content
                      ? project?.bilingual_layout === 'left-right'
                        ? 'flex-row'
                        : 'flex-col'
                      : ''
                  } items-center justify-center p-8 text-center relative z-10 overflow-hidden`}>
                    {/* Primary Content */}
                    <div className={`overflow-hidden max-h-full ${
                      project?.bilingual_mode && currentSlide.secondary_content
                        ? project?.bilingual_layout === 'left-right'
                          ? 'w-1/2 pr-4 border-r border-white/20'
                          : 'flex-1 pb-4'
                        : 'w-full'
                    }`}>
                      {currentSlide.type && currentSlide.type !== 'text' && currentSlide.type !== 'title' && (
                        <div className="mb-4">
                          <span className={currentSlide.type === 'chorus' ? 'chorus-badge' : 'verse-badge'}>
                            {currentSlide.type.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <p 
                        className="font-lyrics leading-relaxed whitespace-pre-line drop-shadow-lg overflow-hidden text-ellipsis"
                        style={{ 
                          fontSize: project?.bilingual_mode && currentSlide.secondary_content
                            ? `clamp(16px, ${currentSlide.font_size * 0.7}px, 4vh)`
                            : `clamp(18px, ${currentSlide.font_size}px, 6vh)`,
                          maxHeight: '100%',
                          display: '-webkit-box',
                          WebkitLineClamp: project?.bilingual_mode ? 8 : 12,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {currentSlide.content}
                      </p>
                    </div>
                    
                    {/* Secondary Content (Bilingual) */}
                    {project?.bilingual_mode && currentSlide.secondary_content && (
                      <div className={`overflow-hidden max-h-full ${
                        project?.bilingual_layout === 'left-right'
                          ? 'w-1/2 pl-4'
                          : 'flex-1 pt-4 border-t border-white/20'
                      }`}>
                        <p 
                          className="font-lyrics leading-relaxed whitespace-pre-line drop-shadow-lg opacity-90 overflow-hidden text-ellipsis"
                          style={{ 
                            fontSize: `clamp(14px, ${Math.max(20, currentSlide.font_size * 0.6)}px, 4vh)`,
                            maxHeight: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: 8,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {currentSlide.secondary_content}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={selectedSlideIndex === 0}
                  onClick={() => setSelectedSlideIndex(Math.max(0, selectedSlideIndex - 1))}
                  data-testid="prev-slide-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                
                <span className="text-sm text-muted-foreground">
                  {selectedSlideIndex + 1} / {project.slides.length}
                </span>
                
                <Button
                  variant="outline"
                  size="icon"
                  disabled={selectedSlideIndex === project.slides.length - 1}
                  onClick={() => setSelectedSlideIndex(Math.min(project.slides.length - 1, selectedSlideIndex + 1))}
                  data-testid="next-slide-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Eye className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select or add a slide to preview</p>
              </div>
            </div>
          )}
        </div>

        {/* Properties Panel (Right) — hidden on mobile; use Edit dialog instead */}
        {currentSlide && (
          <div className="hidden md:block w-72 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 p-4 overflow-y-auto flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Slide Properties</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditSlide({ ...currentSlide });
                  setEditDialogOpen(true);
                }}
                className="gap-1"
                data-testid="edit-slide-btn"
              >
                <Edit2 className="w-3 h-3" />
                Edit
              </Button>
            </div>
            
            <div className="space-y-6">
              {/* Content - with hint for translation fixes */}
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Content</span>
                  <span className="text-xs text-muted-foreground">Edit for translation fixes</span>
                </Label>
                <Textarea
                  value={currentSlide.content}
                  onChange={(e) => {
                    const updatedSlides = [...project.slides];
                    updatedSlides[selectedSlideIndex].content = e.target.value;
                    setProject({ ...project, slides: updatedSlides });
                  }}
                  onBlur={() => saveProject({ slides: project.slides })}
                  className="min-h-[100px] resize-none"
                  data-testid="slide-content-input"
                />
              </div>

              {/* Type */}
              <div className="space-y-2">
                <Label>Slide Type</Label>
                <Select
                  value={currentSlide.type}
                  onValueChange={(value) => {
                    const updatedSlides = [...project.slides];
                    updatedSlides[selectedSlideIndex].type = value;
                    const typeConfig = SLIDE_TYPES.find(t => t.value === value);
                    if (typeConfig) {
                      updatedSlides[selectedSlideIndex].background_color = typeConfig.color;
                    }
                    setProject({ ...project, slides: updatedSlides });
                    saveProject({ slides: updatedSlides });
                  }}
                >
                  <SelectTrigger data-testid="slide-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SLIDE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Background */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Background
                </Label>
                
                {/* Current background preview */}
                <div 
                  className="aspect-video rounded-lg overflow-hidden border relative cursor-pointer hover:ring-2 hover:ring-amber-500 transition-all"
                  onClick={() => setWallpaperDialogOpen(true)}
                  style={{
                    backgroundColor: currentSlide.background_color,
                    ...(currentSlide.background_type === 'gradient' && currentSlide.background_image && {
                      background: currentSlide.background_image
                    })
                  }}
                  data-testid="background-preview"
                >
                  {currentSlide.background_type === 'image' && currentSlide.background_image && (
                    <img 
                      src={currentSlide.background_image} 
                      alt="Background" 
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                    <span className="text-white text-xs font-medium">Click to change</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full gap-2"
                  onClick={() => setWallpaperDialogOpen(true)}
                  data-testid="change-wallpaper-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Change Background
                </Button>
              </div>

              <Separator />

              {/* Colors */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Background Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentSlide.background_color}
                      onChange={(e) => {
                        const updatedSlides = [...project.slides];
                        updatedSlides[selectedSlideIndex].background_color = e.target.value;
                        setProject({ ...project, slides: updatedSlides });
                      }}
                      onBlur={() => saveProject({ slides: project.slides })}
                      className="w-12 h-10 p-1 cursor-pointer"
                      data-testid="bg-color-input"
                    />
                    <Input
                      value={currentSlide.background_color}
                      onChange={(e) => {
                        const updatedSlides = [...project.slides];
                        updatedSlides[selectedSlideIndex].background_color = e.target.value;
                        setProject({ ...project, slides: updatedSlides });
                      }}
                      onBlur={() => saveProject({ slides: project.slides })}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Type className="w-4 h-4" />
                    Text Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentSlide.text_color}
                      onChange={(e) => {
                        const updatedSlides = [...project.slides];
                        updatedSlides[selectedSlideIndex].text_color = e.target.value;
                        setProject({ ...project, slides: updatedSlides });
                      }}
                      onBlur={() => saveProject({ slides: project.slides })}
                      className="w-12 h-10 p-1 cursor-pointer"
                      data-testid="text-color-input"
                    />
                    <Input
                      value={currentSlide.text_color}
                      onChange={(e) => {
                        const updatedSlides = [...project.slides];
                        updatedSlides[selectedSlideIndex].text_color = e.target.value;
                        setProject({ ...project, slides: updatedSlides });
                      }}
                      onBlur={() => saveProject({ slides: project.slides })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Font Size */}
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Font Size</span>
                  <span className="text-muted-foreground">{currentSlide.font_size}px</span>
                </Label>
                <Slider
                  value={[currentSlide.font_size]}
                  min={24}
                  max={96}
                  step={4}
                  onValueChange={([value]) => {
                    const updatedSlides = [...project.slides];
                    updatedSlides[selectedSlideIndex].font_size = value;
                    setProject({ ...project, slides: updatedSlides });
                  }}
                  onValueCommit={() => saveProject({ slides: project.slides })}
                  data-testid="font-size-slider"
                />
              </div>

              <Separator />

              {/* Speaker Notes */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Speaker Notes
                </Label>
                <Textarea
                  placeholder="Add speaker notes for this slide... (visible in presenter view)"
                  value={currentSlide.notes || ''}
                  onChange={(e) => {
                    const updatedSlides = [...project.slides];
                    updatedSlides[selectedSlideIndex].notes = e.target.value;
                    setProject({ ...project, slides: updatedSlides });
                  }}
                  onBlur={() => saveProject({ slides: project.slides })}
                  className="min-h-[80px] text-sm"
                  data-testid="speaker-notes-input"
                />
                <p className="text-xs text-muted-foreground">
                  Notes are visible only in presenter view, not to audience
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Slide Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Slide</DialogTitle>
            <DialogDescription>
              Choose a template or create a custom slide
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="templates" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="custom">Custom Slide</TabsTrigger>
            </TabsList>

            <TabsContent value="templates" className="mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {slideTemplates.map((template) => (
                  <motion.button
                    key={template.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAddFromTemplate(template)}
                    className="flex flex-col items-center p-4 rounded-lg border-2 border-transparent hover:border-amber-500 transition-all text-center"
                    style={{ backgroundColor: template.preview_color + '20' }}
                    data-testid={`template-${template.id}`}
                  >
                    <div 
                      className="w-full aspect-video rounded-lg mb-2 flex items-center justify-center text-white text-xs"
                      style={{ backgroundColor: template.preview_color }}
                    >
                      {template.name.split(' ')[0]}
                    </div>
                    <span className="text-sm font-medium">{template.name}</span>
                    <span className="text-xs text-muted-foreground">{template.description}</span>
                  </motion.button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="custom" className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  placeholder="Enter slide content..."
                  value={editSlide.content}
                  onChange={(e) => setEditSlide({ ...editSlide, content: e.target.value })}
                  className="min-h-[100px]"
                  data-testid="new-slide-content"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={editSlide.type}
                  onValueChange={(value) => {
                    const typeConfig = SLIDE_TYPES.find(t => t.value === value);
                    setEditSlide({ 
                      ...editSlide, 
                      type: value,
                      background_color: typeConfig?.color || "#000000"
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SLIDE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddSlide} data-testid="confirm-add-slide">
                  Add Slide
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Edit Slide Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Slide</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={editSlide.content}
                onChange={(e) => setEditSlide({ ...editSlide, content: e.target.value })}
                className="min-h-[150px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={editSlide.type}
                  onValueChange={(value) => setEditSlide({ ...editSlide, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SLIDE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Font Size</Label>
                <Input
                  type="number"
                  value={editSlide.font_size}
                  onChange={(e) => setEditSlide({ ...editSlide, font_size: parseInt(e.target.value) })}
                  min={24}
                  max={96}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Background</Label>
                <Input
                  type="color"
                  value={editSlide.background_color}
                  onChange={(e) => setEditSlide({ ...editSlide, background_color: e.target.value })}
                  className="h-10 cursor-pointer"
                />
              </div>
              <div className="space-y-2">
                <Label>Text Color</Label>
                <Input
                  type="color"
                  value={editSlide.text_color}
                  onChange={(e) => setEditSlide({ ...editSlide, text_color: e.target.value })}
                  className="h-10 cursor-pointer"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSlide}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Project Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input
                value={project.name}
                onChange={(e) => setProject({ ...project, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={project.description || ""}
                onChange={(e) => setProject({ ...project, description: e.target.value })}
                placeholder="Add a description..."
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Music className="w-4 h-4" />
                Song Mode
              </Label>
              <Switch
                checked={project.is_song}
                onCheckedChange={(checked) => setProject({ ...project, is_song: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Auto-Scroll Speed (seconds per slide)
              </Label>
              <Slider
                value={[project.auto_scroll_speed || 5]}
                min={2}
                max={15}
                step={1}
                onValueChange={([value]) => setProject({ ...project, auto_scroll_speed: value })}
              />
              <p className="text-xs text-muted-foreground">{project.auto_scroll_speed || 5} seconds</p>
            </div>

            <Separator />

            {/* Transition Settings */}
            <div className="space-y-4">
              <Label className="text-sm font-medium">Slide Transitions</Label>
              
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Transition Effect</Label>
                <Select
                  value={project.transition_effect || "fade"}
                  onValueChange={(value) => setProject({ ...project, transition_effect: value })}
                >
                  <SelectTrigger data-testid="transition-effect-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fade">Fade</SelectItem>
                    <SelectItem value="slide">Slide</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="flip">Flip</SelectItem>
                    <SelectItem value="dissolve">Dissolve</SelectItem>
                    <SelectItem value="none">None (Instant)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Transition Duration: {project.transition_duration || 0.3}s</Label>
                <Slider
                  value={[project.transition_duration || 0.3]}
                  min={0.1}
                  max={1.0}
                  step={0.1}
                  onValueChange={([value]) => setProject({ ...project, transition_duration: value })}
                />
              </div>
            </div>

            {/* Custom Branding (Pro+ feature) - Extracted Component */}
            <BrandingSettings
              project={project}
              setProject={setProject}
              onOpenPreview={() => setBrandingPreviewOpen(true)}
              onOpenGallery={() => setBrandingGalleryOpen(true)}
              t={t}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button onClick={() => {
              saveProject({ 
                name: project.name, 
                description: project.description,
                is_song: project.is_song,
                auto_scroll_speed: project.auto_scroll_speed,
                transition_effect: project.transition_effect,
                transition_duration: project.transition_duration,
                branding_enabled: project.branding_enabled,
                branding_logo_url: project.branding_logo_url,
                branding_logo_position: project.branding_logo_position,
                branding_logo_size: project.branding_logo_size,
                branding_logo_opacity: project.branding_logo_opacity,
                branding_text: project.branding_text
              });
              setSettingsDialogOpen(false);
            }}>
              {t('save_settings') || 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Generate Slides with AI
            </DialogTitle>
            <DialogDescription>
              Paste your content and let AI create structured slides.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Paste your document content or song lyrics here..."
              className="min-h-[300px] resize-none"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              data-testid="generate-text-input"
            />

            {isGenerating && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Sparkles className="w-5 h-5 text-amber-500 animate-pulse" />
                <span className="text-sm">AI is generating your slides...</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={isGenerating || !textInput.trim()}
              className="gap-2"
              data-testid="confirm-generate-btn"
            >
              <Sparkles className="w-4 h-4" />
              Generate Slides
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallpaper Selector */}
      <WallpaperSelector
        open={wallpaperDialogOpen}
        onOpenChange={setWallpaperDialogOpen}
        currentBackground={currentSlide?.background_image || currentSlide?.background_color}
        currentBackgroundType={currentSlide?.background_type || 'color'}
        currentBackgroundVideo={currentSlide?.background_video}
        onSelect={(bgData) => {
          const updatedSlides = [...project.slides];
          updatedSlides[selectedSlideIndex] = {
            ...updatedSlides[selectedSlideIndex],
            ...bgData
          };
          setProject({ ...project, slides: updatedSlides });
          saveProject({ slides: updatedSlides });
        }}
        onApplyToAll={async (bgData) => {
          try {
            await axios.post(`${API}/projects/${projectId}/apply-background-all`, bgData);
            // Refresh project data
            const response = await axios.get(`${API}/projects/${projectId}`);
            setProject(response.data);
            toast.success("Background applied to all slides!");
          } catch (error) {
            toast.error("Failed to apply background to all slides");
          }
        }}
        projectId={projectId}
      />

      {/* Translation Dialog */}
      <Dialog open={translateDialogOpen} onOpenChange={setTranslateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="w-5 h-5 text-amber-500" />
              {t('translate')}
            </DialogTitle>
            <DialogDescription>
              Translate slides to another language using AI
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Target Language Selection */}
            <div className="space-y-2">
              <Label>{t('target_language')}</Label>
              <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                <SelectTrigger data-testid="target-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LANGUAGES).map(([code, lang]) => (
                    <SelectItem key={code} value={code}>
                      {lang.flag} {lang.nativeName} ({lang.name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bilingual Mode Toggle */}
            <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <Globe className="w-4 h-4" />
                  {t('bilingual_mode')}
                </Label>
                <Switch
                  checked={project?.bilingual_mode || false}
                  onCheckedChange={toggleBilingualMode}
                  data-testid="bilingual-mode-switch"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('show_both')} - Display original and translated text on slides
              </p>

              {project?.bilingual_mode && (
                <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                  <Label>{t('bilingual_layout')}</Label>
                  <Select 
                    value={project?.bilingual_layout || 'top-bottom'} 
                    onValueChange={setBilingualLayout}
                  >
                    <SelectTrigger data-testid="bilingual-layout-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-bottom">{t('layout_top_bottom')}</SelectItem>
                      <SelectItem value="left-right">{t('layout_left_right')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Current Slide Preview */}
            {currentSlide && (
              <div className="space-y-2">
                <Label>Current Slide Content</Label>
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm max-h-32 overflow-y-auto">
                  {currentSlide.content}
                </div>
                {currentSlide.secondary_content && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm max-h-32 overflow-y-auto border-l-4 border-amber-500">
                    <span className="text-xs text-amber-600 dark:text-amber-400 font-medium block mb-1">
                      {LANGUAGES[currentSlide.secondary_language]?.nativeName || 'Translation'}
                    </span>
                    {currentSlide.secondary_content}
                  </div>
                )}
              </div>
            )}

            {isTranslating && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Languages className="w-5 h-5 text-amber-500 animate-pulse" />
                <span className="text-sm">{t('translating')}</span>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setTranslateDialogOpen(false)}
              className="sm:order-1"
            >
              {t('cancel')}
            </Button>
            <Button 
              variant="outline"
              onClick={handleTranslateAll}
              disabled={isTranslating}
              className="gap-2 sm:order-2"
              data-testid="translate-all-btn"
            >
              <Languages className="w-4 h-4" />
              {t('translate_all')}
            </Button>
            <Button 
              onClick={handleTranslateSlide}
              disabled={isTranslating || !currentSlide}
              className="gap-2 sm:order-3"
              data-testid="translate-slide-btn"
            >
              <Languages className="w-4 h-4" />
              {t('translate_slide')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Branding Preview Dialog - Extracted Component */}
      <BrandingPreviewModal
        open={brandingPreviewOpen}
        onClose={() => setBrandingPreviewOpen(false)}
        project={project}
        setProject={setProject}
        t={t}
      />

      {/* Branding Template Gallery */}
      <BrandingTemplateGallery
        open={brandingGalleryOpen}
        onClose={() => setBrandingGalleryOpen(false)}
        currentLogo={project.branding_logo_url}
        onApply={(templateSettings) => {
          setProject({
            ...project,
            ...templateSettings
          });
          // Auto-enable branding if a template is applied (except "no-branding")
          if (templateSettings.branding_enabled !== false) {
            setProject(prev => ({ ...prev, branding_enabled: true, ...templateSettings }));
          }
        }}
      />

      <SharePresentationDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        projectId={projectId}
        projectName={project?.name}
      />

      <ProjectMembersDialog
        open={membersDialogOpen}
        onOpenChange={setMembersDialogOpen}
        projectId={projectId}
        projectName={project?.name}
      />

      <ProjectCommentsDialog
        open={commentsDialogOpen}
        onOpenChange={setCommentsDialogOpen}
        projectId={projectId}
        projectName={project?.name}
        currentSlideIndex={selectedSlideIndex ?? null}
      />
    </div>
  );
};

export default Editor;
